<template>
    <div class="container-fluid">
        <nav-header :navItems="[{ name: 'Discover', link: { name: 'Discover My Data' } }]"
            pretitle="aqfer Data Discovery" title="Discover My Data" />

        <!-- ── Source: mode switch and pickers in one compact card ──────────── -->
        <div class="card shadow-sm border-0 mb-2 dmd-toolbar-card">
            <div class="card-body dmd-toolbar-body">
                <div class="dmd-mode-switch mb-3">
                    <button type="button" class="dmd-mode-btn" :class="{ active: mode === 'catalog' }"
                        :disabled="running || !catalogSupported"
                        :title="catalogSupported ? '' : 'The data catalog is only available on AWS (Glue).'"
                        @click="setMode('catalog')">
                        <i class="fa fa-database" aria-hidden="true"></i> Catalog First
                    </button>
                    <button type="button" class="dmd-mode-btn" :class="{ active: mode === 'files' }" :disabled="running"
                        @click="setMode('files')">
                        <i class="fa fa-folder-open" aria-hidden="true"></i> Scan Files
                    </button>
                    <span class="dmd-mode-hint">
                        {{ mode === 'catalog'
                            ? 'Fast, bulk — only sees data already registered in the Glue Catalog.'
                            : `Scans ${storageLabel} directly. Parquet only, sampled from files up to ${maxFileLabel}.` }}
                        <template v-if="!catalogSupported"> Catalog First is not available on this cloud.</template>
                    </span>
                </div>

                <!-- catalog first -->
                <div v-if="mode === 'catalog'" class="dmd-controls-grid">
                    <div class="dmd-field">
                        <label class="dmd-label">Credential
                            <i class="fa fa-circle-info text-muted ms-1" aria-hidden="true"
                                title="Fills the job stack's data path; the catalog doesn't carry the credential."></i>
                        </label>
                        <InputField type="customSingleSelect" :showLabel="false" :options="credentialOptions"
                            :createOption="false" :canClear="false"
                            :editable="!running && !credentialLoading && !!credentialOptions.length"
                            :placeholder="credentialPlaceholder" v-model="credentialName" />
                    </div>
                    <div class="dmd-field">
                        <label class="dmd-label">Database</label>
                        <InputField type="customSingleSelect" :showLabel="false" :options="databaseOptions"
                            :createOption="false" :canClear="false"
                            :editable="!running && !databasesLoading && !!databaseOptions.length"
                            :placeholder="databasesLoading ? 'Loading databases…' : 'Select a database'" v-model="database" />
                    </div>
                    <div class="dmd-field">
                        <label class="dmd-label">Table</label>
                        <InputField type="customSingleSelect" :showLabel="false" :options="tableOptions"
                            :createOption="false" :canClear="true"
                            :editable="!running && !!database && !tablesLoading"
                            :placeholder="tablesLoading ? 'Loading tables…' : 'All tables'" v-model="tableModel" />
                    </div>
                    <button type="button" class="btn btn-primary dmd-scan-btn" :disabled="!canScanCatalog" @click="run">
                        <span v-if="running" class="spinner-border spinner-border-sm me-1" role="status" />
                        <i v-else class="fa fa-bolt me-1" aria-hidden="true"></i>
                        <span>{{ running ? 'Scanning…' : 'Scan Catalog' }}</span>
                    </button>
                </div>

                <!-- scan files -->
                <template v-else>
                    <div class="dmd-controls-grid dmd-controls-grid--files">
                        <div class="dmd-field">
                            <label class="dmd-label">Credential</label>
                            <InputField type="customSingleSelect" :showLabel="false" :options="credentialOptions"
                                :createOption="false" :canClear="false"
                                :editable="!running && !credentialLoading && !!credentialOptions.length"
                                :placeholder="credentialPlaceholder" v-model="credentialName" />
                        </div>
                        <div class="dmd-field">
                            <label class="dmd-label">Bucket</label>
                            <InputField type="customSingleSelect" :showLabel="false" :options="bucketOptions"
                                :createOption="false" :canClear="false"
                                :editable="!running && !bucketsLoading && !!bucketOptions.length"
                                :placeholder="bucketPlaceholder" v-model="bucket" />
                        </div>
                        <div class="dmd-field dmd-folder">
                            <label class="dmd-label">Folder <span class="fw-normal text-muted">(optional)</span></label>
                            <div class="input-group">
                                <input v-model.trim="prefix" type="text" class="form-control" placeholder="prefix/"
                                    :disabled="running || !bucket" @focus="subfolderMenuOpen = false" />
                                <button type="button"
                                    :class="['btn', $store.state.darkMode ? 'btn-secondary' : 'btn-outline-secondary']"
                                    :disabled="running || !bucket || subfoldersLoading" @click="toggleSubfolderMenu">
                                    <span v-if="subfoldersLoading" class="spinner-border spinner-border-sm"></span>
                                    <span v-else>Browse</span>
                                </button>
                            </div>

                            <div v-if="subfolderMenuOpen" class="dmd-subfolder-menu">
                                <input v-model="subfolderSearch" type="text" class="form-control form-control-sm mb-1"
                                    placeholder="Search folders…" />
                                <button v-if="prefix" type="button" class="dmd-subfolder-item dmd-subfolder-item--up"
                                    @click="goUpSubfolder">
                                    <i class="fa fa-arrow-up me-1" aria-hidden="true"></i> Up one level
                                </button>
                                <div v-if="subfolderError" class="dmd-subfolder-msg text-danger">{{ subfolderError }}</div>
                                <div v-else-if="subfoldersLoading" class="dmd-subfolder-msg text-muted">Loading…</div>
                                <div v-else-if="!filteredSubfolders.length" class="dmd-subfolder-msg text-muted">
                                    {{ subfolderSearch ? 'No matching folders.' : 'No subfolders here.' }}
                                </div>
                                <button v-for="opt in filteredSubfolders" :key="opt" type="button" class="dmd-subfolder-item"
                                    @click="selectSubfolder(opt)">
                                    <i class="fa fa-folder me-1" aria-hidden="true"></i>{{ opt }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- prompt and action on one line, as in Map My Data -->
                    <div class="dmd-field mt-3">
                        <label class="dmd-label" for="dmd-prompt">What are you looking for?</label>
                        <div class="dmd-prompt-row">
                            <input id="dmd-prompt" v-model="scanPrompt" type="text" class="form-control"
                                :disabled="running" placeholder="e.g. which of these could feed an impressions pipeline?"
                                @keyup.enter="canScanFiles && run()" />
                            <button type="button" class="btn btn-primary dmd-scan-btn" :disabled="!canScanFiles" @click="run">
                                <span v-if="running" class="spinner-border spinner-border-sm me-1" role="status" />
                                <i v-else class="fa fa-bolt me-1" aria-hidden="true"></i>
                                <span>{{ running ? 'Scanning…' : 'Discover' }}</span>
                            </button>
                        </div>
                        <div class="small text-muted mt-1">
                            {{ filesLocationReady && !scanPrompt.trim()
                                ? "Tell the agent what you're looking for to enable Discover."
                                : scanLocationLabel }}
                        </div>
                    </div>
                </template>

                <div v-if="toolbarError" class="alert alert-warning small py-1 px-3 mt-3 mb-0">{{ toolbarError }}</div>
            </div>
        </div>

        <div class="text-muted text-end mb-2 dmd-ai-note">
            <i class="fa fa-circle-info me-1" aria-hidden="true"></i>This tool uses AI, which can make mistakes. Verify important outputs before acting on them.
        </div>

        <!-- ── Results ────────────────────────────────────────────────────── -->
        <div class="discover-results-area">

            <!-- Error banner -->
            <div v-if="errorMessage" class="alert alert-danger mb-3" role="alert">{{ errorMessage }}</div>

            <!-- Agent steps, same timeline as Map My Data -->
            <div v-if="statusList.length || running" class="mb-2">
                <div class="dmd-agent-status" role="button" @click="statusOpen = !statusOpen">
                    <i v-if="running" class="fa-solid fa-circle fa-beat-fade text-success fs-5"></i>
                    <i v-else class="fa-solid fa-check text-success"></i>
                    <span class="status-text text-muted fst-italic fw-bold">{{ currentStatusText }}</span>
                    <i :class="['fa-solid text-muted', statusOpen ? 'fa-angle-up' : 'fa-angle-down']"
                        style="font-size: 0.75rem; margin-top: 0.15rem;"></i>
                    <button v-if="running" type="button" class="btn btn-sm btn-outline-secondary ms-auto"
                        @click.stop="cancel">Cancel</button>
                </div>
                <div v-show="statusOpen" class="status-text text-muted mt-2 dmd-status-list">
                    <div v-for="(status, index) in statusList" :key="index" class="row py-2 mx-0 border-bottom">
                        <div class="col-2 px-0 pe-2 d-flex align-items-top" style="word-break: normal;">
                            <span class="d-inline-flex justify-content-center me-2" style="width: 1.3rem;">
                                <i :class="getAgentStatusIcon(status.label, running && runningSteps[index] === true)"></i>
                            </span>
                            <span class="fw-bold">{{ status.label }}</span>
                        </div>
                        <div class="col-8 px-0"><span>{{ status.detail }}</span></div>
                        <div class="col-2 text-end px-0"><span>{{ status.timestamp }}</span></div>
                    </div>
                </div>
            </div>

            <!-- Loading card, styled as in Map My Data; one result, so one loader -->
            <div v-if="running" class="card mb-3">
                <div class="text-center pt-4 pb-2">
                    <div class="spinner-border mb-2" role="status" style="width: 1.5em; height: 1.5em; opacity: 0.5;">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="text-muted mb-0">{{ mode === 'catalog' ? 'Checking Tables...' : 'Discovering Datasets...' }}</p>
                </div>
                <div :class="[$store.state.darkMode ? 'loading-content-card-dark' : 'loading-content-card']">
                    <div class="content"><h2></h2><p></p></div>
                </div>
            </div>

            <!-- Results tabs -->
            <div v-else-if="result">

                <!-- Scan totals, one slim bar -->
                <div v-if="result.scan?.listing_truncated" class="alert alert-warning small py-2 px-3 mb-2">
                    <i class="fa fa-triangle-exclamation me-1" aria-hidden="true"></i>
                    Only the first {{ result.scan.objects_listed.toLocaleString() }} objects under this prefix were
                    scanned. Narrow the prefix to see the rest.
                </div>
                <div class="dmd-statbar mb-3">
                    <div v-for="st in statItems" :key="st.label" class="dmd-stat" :class="st.tone && `dmd-stat--${st.tone}`">
                        <span class="dmd-stat-value">{{ st.value }}</span>
                        <span class="dmd-stat-label">{{ st.label }}</span>
                    </div>
                </div>

                <Tabs :tabs="tabItems" :btnTabs="true">

                    <!-- Summary -->
                    <template v-slot:summary>
                        <div class="card">
                            <div class="card-body markdown-content discover-markdown">
                                <Markdown :content="result.summary_markdown || ''" />
                            </div>
                        </div>
                    </template>

                    <!-- Datasets (Scan Files mode) -->
                    <template v-slot:datasets>
                        <div v-if="!datasets.length" class="card">
                            <div class="card-body text-center text-muted py-5">No Parquet datasets found for this scope.</div>
                        </div>
                        <div class="dmd-ds-list">
                        <div v-for="ds in datasets" :key="ds.path" class="card dmd-ds-card"
                            :class="{ 'dmd-ds-card--ready': ds.zcr?.ready }">
                            <div class="card-body">
                                <!-- header: type icon, name, location | badges, action -->
                                <div class="dmd-ds-head">
                                    <div class="dmd-ds-icon" :class="`dmd-ds-icon--${ds.data_type || 'unknown'}`">
                                        <i :class="['fa', typeIcon(ds.data_type)]" aria-hidden="true"></i>
                                    </div>
                                    <div class="dmd-ds-title">
                                        <h5 class="mb-0">{{ datasetName(ds.path) }}</h5>
                                        <div class="dmd-ds-path">
                                            <code>{{ storageUri(ds.path) }}</code>
                                            <router-link v-if="fileBrowserRoute" :to="fileBrowserRoute" class="ms-2"
                                                title="Open bucket in File Browser">
                                                <i class="fa fa-arrow-up-right-from-square" aria-hidden="true"></i>
                                            </router-link>
                                        </div>
                                    </div>
                                    <div class="dmd-ds-badges">
                                        <span v-if="ds.data_type" class="ds-badge ds-badge--type">{{ formatDataType(ds.data_type) }}</span>
                                        <span v-if="recheckState(ds).userSet" class="ds-badge ds-badge--format">
                                            <i class="fa fa-user-pen me-1" aria-hidden="true"></i>Changed by you
                                        </span>
                                        <span v-if="ds.zcr?.ready" class="ds-badge ds-badge--zcr-ok">
                                            <i class="fa fa-circle-check me-1" aria-hidden="true"></i>ZCR ready
                                        </span>
                                        <span v-else class="ds-badge ds-badge--zcr-warn">
                                            <i class="fa fa-triangle-exclamation me-1" aria-hidden="true"></i>Not ready
                                        </span>
                                        <button v-if="!recheckState(ds).editing" type="button"
                                            class="btn btn-sm btn-outline-secondary dmd-ds-action"
                                            :disabled="running" @click="startRecheck(ds)">
                                            <i class="fa fa-pen me-1" aria-hidden="true"></i>Change type
                                        </button>
                                    </div>
                                </div>

                                <p v-if="ds.classification_reason" class="dmd-ds-reason">{{ ds.classification_reason }}</p>
                                <p v-if="typeHintDiffers(ds)" class="dmd-ds-note">
                                    <i class="fa fa-circle-info me-1" aria-hidden="true"></i>The column names look like
                                    {{ formatDataType(ds.details.heuristic_data_type) }} data, but the sample rows look like
                                    {{ formatDataType(ds.data_type) }}. Check Preview rows to confirm.
                                </p>

                                <!-- User-chosen data type / columns, rechecked by the service -->
                                <div v-if="recheckState(ds).editing" class="dmd-recheck mb-3">
                                    <div class="row g-2 align-items-end">
                                        <div class="col-12 col-md-4">
                                            <label class="form-label small fw-semibold mb-1">Data type</label>
                                            <select v-model="recheckState(ds).data_type" class="form-select form-select-sm"
                                                :disabled="recheckState(ds).busy">
                                                <option v-for="t in editableTypes" :key="t" :value="t">{{ formatDataType(t) }}</option>
                                            </select>
                                        </div>
                                        <div class="col-6 col-md-3">
                                            <label class="form-label small fw-semibold mb-1">Identity column</label>
                                            <select v-model="recheckState(ds).identity_column" class="form-select form-select-sm"
                                                :disabled="recheckState(ds).busy">
                                                <option value="">—</option>
                                                <option v-for="c in ds.columns" :key="c" :value="c">{{ c }}</option>
                                            </select>
                                        </div>
                                        <div class="col-6 col-md-3">
                                            <label class="form-label small fw-semibold mb-1">
                                                Timestamp column<span v-if="recheckState(ds).data_type === 'data_subject_profile'" class="text-muted fw-normal"> (not needed)</span>
                                            </label>
                                            <select v-model="recheckState(ds).timestamp_column" class="form-select form-select-sm"
                                                :disabled="recheckState(ds).busy">
                                                <option value="">—</option>
                                                <option v-for="c in ds.columns" :key="c" :value="c">{{ c }}</option>
                                            </select>
                                        </div>
                                        <div class="col-12 col-md-2 d-flex gap-2">
                                            <button type="button" class="btn btn-primary btn-sm" :disabled="recheckState(ds).busy"
                                                @click="recheck(ds)">
                                                <span v-if="recheckState(ds).busy" class="spinner-border spinner-border-sm me-1"></span>
                                                {{ recheckState(ds).busy ? 'Checking…' : 'Recheck' }}
                                            </button>
                                            <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="recheckState(ds).busy"
                                                @click="recheckState(ds).editing = false">Cancel</button>
                                        </div>
                                    </div>
                                    <div class="small text-muted mt-2">
                                        Recheck recomputes ZCR readiness and the job stack for your choice. It does not re-read the bucket.
                                    </div>
                                    <div v-if="recheckState(ds).error" class="alert alert-danger small py-2 px-3 mt-2 mb-0">
                                        {{ recheckState(ds).error }}
                                    </div>
                                </div>

                                <!-- facts: layout, sample and the ZCR mapping in one row -->
                                <div class="dmd-facts">
                                    <div v-if="ds.details" class="dmd-fact">
                                        <span class="ds-label">Files</span>
                                        <span>{{ ds.details.file_count }} · {{ formatBytes(ds.details.total_size_bytes) }}</span>
                                    </div>
                                    <div v-if="ds.details" class="dmd-fact">
                                        <span class="ds-label">Sample</span>
                                        <span :title="sampleFileName(ds)">
                                            {{ ds.details.row_count_sampled }} rows · {{ formatBytes(ds.details.sample_size_bytes) }}
                                        </span>
                                    </div>
                                    <div v-if="ds.details?.partition_keys?.length" class="dmd-fact">
                                        <span class="ds-label">Partitions</span>
                                        <span><code v-for="k in ds.details.partition_keys" :key="k" class="me-1">{{ k }}</code></span>
                                    </div>
                                    <div class="dmd-fact">
                                        <span class="ds-label">Identity</span>
                                        <code v-if="ds.identity_column">{{ ds.identity_column }}</code>
                                        <span v-else class="text-muted">—</span>
                                    </div>
                                    <div class="dmd-fact">
                                        <span class="ds-label">Timestamp</span>
                                        <code v-if="ds.timestamp_column">{{ ds.timestamp_column }}</code>
                                        <span v-else class="text-muted">—</span>
                                    </div>
                                    <div v-if="ds.zcr?.schema" class="dmd-fact">
                                        <span class="ds-label">ZCR schema</span>
                                        <code>{{ ds.zcr.schema }}</code>
                                    </div>
                                </div>
                                <ul v-if="!ds.zcr?.ready && ds.zcr?.issues?.length" class="dmd-issues">
                                    <li v-for="issue in ds.zcr.issues" :key="issue">{{ issue }}</li>
                                </ul>

                                <div v-if="ds.columns?.length" class="mt-3">
                                    <div class="ds-label">Columns ({{ ds.columns.length }})</div>
                                    <div class="d-flex flex-wrap gap-1 mt-1">
                                        <span v-for="col in ds.columns" :key="col" class="ds-col-badge"
                                            :class="{ 'ds-col-badge--key': col === ds.identity_column || col === ds.timestamp_column }">{{ col }}</span>
                                    </div>
                                </div>

                                <!-- Column profile and sample rows, from the scan itself -->
                                <div v-if="ds.details" class="mt-3">
                                    <div class="deploy-config-tabs">
                                        <button type="button" class="deploy-config-tab" :class="{ active: panels[ds.path] === 'columns' }"
                                            @click="togglePanel(ds.path, 'columns')">
                                            <i class="fa fa-list me-1" aria-hidden="true"></i>Column profile
                                        </button>
                                        <button type="button" class="deploy-config-tab" :class="{ active: panels[ds.path] === 'preview' }"
                                            @click="togglePanel(ds.path, 'preview')">
                                            <i class="fa fa-table-cells me-1" aria-hidden="true"></i>Preview rows ({{ ds.details.sample_rows.length }})
                                        </button>
                                        <span class="dmd-sample-file ms-auto" :title="'Profiled from ' + sampleFileName(ds)">
                                            <i class="fa fa-file-lines me-1" aria-hidden="true"></i>{{ sampleFileName(ds) }}
                                        </span>
                                    </div>
                                    <div v-if="panels[ds.path] && ds.details.columns_truncated" class="small text-muted mt-2">
                                        Showing the first {{ ds.details.columns.length }} of {{ ds.columns.length }} columns.
                                    </div>
                                    <div v-if="panels[ds.path] === 'columns'" class="dmd-detail-table mt-2">
                                        <table class="table table-sm mb-0">
                                            <thead><tr><th>Column</th><th>Type</th><th>Empty</th><th>Examples</th></tr></thead>
                                            <tbody>
                                                <tr v-for="c in ds.details.columns" :key="c.name">
                                                    <td>
                                                        <code>{{ c.name }}</code>
                                                        <span v-if="c.name === ds.identity_column" class="ds-badge ds-badge--zcr-ok ms-1">identity</span>
                                                        <span v-if="c.name === ds.timestamp_column" class="ds-badge ds-badge--zcr-ok ms-1">timestamp</span>
                                                    </td>
                                                    <td class="text-muted">{{ c.type }}</td>
                                                    <td :class="{ 'text-warning': c.nulls }">{{ emptyPercent(c, ds.details.row_count_sampled) }}</td>
                                                    <td class="text-muted small dmd-examples">{{ (c.examples || []).join(' · ') }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div v-if="panels[ds.path] === 'preview'" class="dmd-detail-table mt-2">
                                        <table class="table table-sm table-nowrap mb-0">
                                            <thead><tr><th v-for="c in ds.columns" :key="c">{{ c }}</th></tr></thead>
                                            <tbody>
                                                <tr v-for="(row, i) in ds.details.sample_rows" :key="i">
                                                    <td v-for="c in ds.columns" :key="c" class="small">{{ formatCell(row[c]) }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        </div>
                    </template>

                    <!-- Tables (Catalog First mode) -->
                    <template v-slot:tables>
                        <div v-if="!catalogTables.length" class="card">
                            <div class="card-body text-center text-muted py-5">No tables found in the catalog.</div>
                        </div>
                        <template v-else>
                            <div class="dmd-table-toolbar mb-3">
                                <div class="dmd-filter-chips">
                                    <button v-for="f in tableStatusFilters" :key="f.key" type="button"
                                        class="dmd-chip" :class="{ active: tableStatusFilter === f.key }"
                                        @click="tableStatusFilter = f.key">
                                        {{ f.label }} <span class="dmd-chip-count">{{ f.count }}</span>
                                    </button>
                                </div>
                                <div class="dmd-table-search">
                                    <i class="fa fa-magnifying-glass" aria-hidden="true"></i>
                                    <input v-model.trim="tableSearchQuery" type="search"
                                        class="form-control form-control-sm" placeholder="Filter by name…" />
                                </div>
                            </div>

                            <div v-if="!visibleTables.length" class="card">
                                <div class="card-body text-center text-muted py-5">
                                    No tables match this filter.
                                </div>
                            </div>

                            <div v-for="t in visibleTables" :key="tableKey(t)" class="card mb-3 dmd-table-card"
                                :class="t.lookup?.ready
                                    ? 'dmd-card-accent dmd-card-accent--ready'
                                    : 'dmd-card-accent'">
                                <div class="card-body">
                                    <div class="d-flex align-items-start justify-content-between gap-2 flex-wrap mb-2">
                                        <h5 class="mb-1 fw-semibold">{{ tableKey(t) }}</h5>
                                        <div class="d-flex gap-2 flex-wrap flex-shrink-0">
                                            <span class="ds-badge ds-badge--format">{{ (t.classification || 'unknown').toUpperCase() }}</span>
                                            <span v-if="t.iceberg_managed" class="ds-badge ds-badge--type">ICEBERG</span>
                                        </div>
                                    </div>
                                    <div class="mb-3">
                                        <div class="ds-label">Location (from catalog)</div>
                                        <code class="ds-location">{{ t.location || '—' }}</code>
                                    </div>
                                    <div v-if="t.details" class="dmd-stats mb-3">
                                        <span v-if="t.details.table_type === 'VIRTUAL_VIEW'" class="text-warning">
                                            <i class="fa fa-eye me-1" aria-hidden="true"></i>View — has no files of its own, so it can't be indexed
                                        </span>
                                        <span v-else-if="t.details.table_type"><i class="fa fa-table me-1" aria-hidden="true"></i>{{ formatTableType(t.details.table_type) }}</span>
                                        <span v-if="t.details.record_count != null"><i class="fa fa-list-ol me-1" aria-hidden="true"></i>{{ t.details.record_count.toLocaleString() }} records</span>
                                        <span v-if="t.details.size_bytes != null"><i class="fa fa-hard-drive me-1" aria-hidden="true"></i>{{ formatBytes(t.details.size_bytes) }}</span>
                                        <span v-if="t.details.object_count != null"><i class="fa fa-file me-1" aria-hidden="true"></i>{{ t.details.object_count }} objects</span>
                                        <span v-if="t.details.partition_keys?.length">
                                            <i class="fa fa-sitemap me-1" aria-hidden="true"></i>partitioned by
                                            <code v-for="k in t.details.partition_keys" :key="k" class="me-1">{{ k }}</code>
                                        </span>
                                    </div>
                                    <div v-if="t.details?.columns?.length" class="mb-3">
                                        <button type="button" class="deploy-config-tab" :class="{ active: panels[tableKey(t)] === 'columns' }"
                                            @click="togglePanel(tableKey(t), 'columns')">
                                            <i class="fa fa-list me-1" aria-hidden="true"></i>Columns ({{ (t.details.column_count || t.details.columns.length).toLocaleString() }})
                                        </button>
                                        <div v-if="panels[tableKey(t)] === 'columns' && t.details.column_count > t.details.columns.length"
                                            class="small text-muted mt-2">
                                            Showing the first {{ t.details.columns.length }} of {{ t.details.column_count.toLocaleString() }} columns.
                                        </div>
                                        <div v-if="panels[tableKey(t)] === 'columns'" class="dmd-detail-table mt-2">
                                            <table class="table table-sm mb-0">
                                                <thead><tr><th>Column</th><th>Type</th></tr></thead>
                                                <tbody>
                                                    <tr v-for="c in t.details.columns" :key="c.name">
                                                        <td>
                                                            <code>{{ c.name }}</code>
                                                            <span v-if="c.name === t.lookup_key" class="ds-badge ds-badge--zcr-ok ms-1">lookup key</span>
                                                        </td>
                                                        <td class="text-muted small dmd-examples">{{ c.type }}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div class="zcr-check-card">
                                        <div class="d-flex align-items-center justify-content-between mb-1">
                                            <span class="ds-label mb-0"><i class="fa fa-bolt me-1" aria-hidden="true"></i>ZCR Lookup Index</span>
                                            <span v-if="t.lookup?.ready" class="ds-badge ds-badge--zcr-ok">
                                                <i class="fa fa-circle-check me-1"></i>Ready
                                            </span>
                                            <span v-else class="ds-badge ds-badge--zcr-warn">Not Ready</span>
                                        </div>
                                        <div v-if="t.lookup?.ready" class="small text-muted">
                                            primary key: <code>{{ t.lookup_key }}</code>
                                        </div>
                                        <div v-else-if="t.iceberg_managed" class="small text-muted">
                                            <i class="fa fa-triangle-exclamation me-1" aria-hidden="true"></i>
                                            Iceberg-managed — direct-Parquet lookup doesn't apply here.
                                        </div>
                                        <ul v-else class="mb-0 ps-3 small text-muted">
                                            <li v-for="issue in t.lookup?.issues || []" :key="issue">{{ issue }}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </template>

                    <!-- Skipped (Scan Files mode) -->
                    <template v-slot:skipped>
                        <div v-if="!skipped.length" class="card">
                            <div class="card-body text-center text-muted py-5">Nothing was skipped.</div>
                        </div>
                        <div v-for="group in skippedGroups" :key="group.reason" class="card mb-3">
                            <div class="card-body">
                                <div class="d-flex align-items-center justify-content-between mb-1">
                                    <span class="fw-semibold">{{ group.label }}</span>
                                    <span class="ds-badge ds-badge--zcr-warn">{{ group.items.length }}</span>
                                </div>
                                <p class="text-muted small mb-2">{{ group.help }}</p>
                                <div class="dmd-file-list">
                                    <div v-for="s in group.items" :key="s.path" class="dmd-file-row">
                                        <code class="small">{{ s.path }}</code>
                                        <span v-if="s.size_bytes != null" class="text-muted small">{{ formatBytes(s.size_bytes) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- Deploy Pipeline -->
                    <template v-slot:deploy>
                        <div v-if="!deployableItems.length" class="card">
                            <div class="card-body text-center text-muted py-5">
                                No ZCR-ready job stacks available for deployment yet.
                            </div>
                        </div>
                        <div v-else-if="!canDeploy" class="alert alert-light small py-2 px-3 mb-3">
                            <i class="fa fa-lock me-1" aria-hidden="true"></i>
                            Creating job stacks requires the Aqfer admin or manager role. Share the configuration
                            below with your Aqfer contact.
                        </div>
                        <div v-for="item in deployableItems" :key="item.stack.stack_name" class="card mb-3">
                            <div class="card-header border-0 d-flex align-items-center justify-content-between">
                                <div class="d-flex align-items-center gap-2">
                                    <i class="fa fa-rocket text-primary"></i>
                                    <span class="fw-semibold">{{ item.label }}</span>
                                    <span class="ds-badge ds-badge--type">{{ item.kind }}</span>
                                </div>
                                <span class="ds-badge ds-badge--zcr-ok">
                                    <i class="fa fa-circle-check me-1"></i>ZCR Ready
                                </span>
                            </div>
                            <div class="card-body">
                                <div class="deploy-pipeline-name mb-3">
                                    <i class="fa fa-layer-group me-2 text-muted"></i>
                                    Pipeline: <code>{{ item.stack.stack_name }}</code>
                                    <span class="text-muted small ms-2">{{ item.stack.create_request.jobs.join(' → ') }}</span>
                                </div>

                                <!-- Generated vars; placeholders must be replaced before deploying -->
                                <div v-for="(o, i) in deployState(item).overrides" :key="o.name" class="mb-2">
                                    <div class="ds-label">
                                        {{ o.name }} → {{ o.dst_name }} · schedule {{ o.schedule }}
                                    </div>
                                    <textarea v-model="deployState(item).overrides[i].vars"
                                        class="form-control form-control-sm discover-vars"
                                        :rows="o.vars.split('\n').length + 1"
                                        :disabled="!canDeploy || deployState(item).creating || deployState(item).deploying || deployState(item).deployed"></textarea>
                                </div>
                                <div v-if="remainingPlaceholders(item).length" class="small text-warning mb-3">
                                    <i class="fa fa-pen-to-square me-1" aria-hidden="true"></i>
                                    Replace before deploying:
                                    <code v-for="p in remainingPlaceholders(item)" :key="p" class="me-1">{{ p }}</code>
                                </div>

                                <div class="mb-3">
                                    <button class="btn btn-sm btn-outline-secondary" type="button"
                                        @click="deployState(item).showPreview = !deployState(item).showPreview">
                                        <i class="fa fa-eye me-1"></i>
                                        {{ deployState(item).showPreview ? 'Hide' : 'Show' }} Job Stack Config
                                    </button>
                                    <button class="btn btn-sm btn-outline-secondary ms-2" type="button"
                                        title="Copy the create and deploy requests as JSON" @click="copyStack(item)">
                                        <i class="fa fa-copy me-1"></i>Copy
                                    </button>
                                    <button class="btn btn-sm btn-outline-secondary ms-2" type="button"
                                        title="Download the create and deploy requests as JSON" @click="downloadStack(item)">
                                        <i class="fa fa-download me-1"></i>Download
                                    </button>
                                    <div v-if="deployState(item).showPreview" class="mt-2">
                                        <div class="deploy-config-tabs mb-2">
                                            <button v-for="tab in ['Create Request', 'Deploy Request']" :key="tab"
                                                class="deploy-config-tab"
                                                :class="{ active: deployState(item).previewTab === tab }"
                                                @click="deployState(item).previewTab = tab">{{ tab }}</button>
                                        </div>
                                        <pre class="discover-code-block">{{ deployState(item).previewTab === 'Create Request'
                                            ? JSON.stringify(item.stack.create_request, null, 2)
                                            : JSON.stringify(deployBody(item), null, 2) }}</pre>
                                    </div>
                                </div>

                                <div v-if="deployState(item).deployed"
                                    class="alert alert-success d-flex align-items-center gap-2 mb-0">
                                    <i class="fa fa-circle-check fa-lg"></i>
                                    <span>Pipeline <strong>{{ item.stack.stack_name }}</strong>
                                        deployed to <strong>{{ deployState(item).sandboxName }}</strong>.</span>
                                    <router-link :to="{ name: 'Sandbox Pipelines', params: { cid, sandboxId: deployState(item).sandboxName } }"
                                        class="ms-auto small">View sandbox pipelines</router-link>
                                </div>
                                <template v-else-if="canDeploy">
                                    <div class="row g-2 align-items-end">
                                        <div class="col">
                                            <label class="form-label small fw-semibold mb-1">Target Sandbox</label>
                                            <select class="form-select form-select-sm"
                                                v-model="deployState(item).sandboxName"
                                                :disabled="deployState(item).creating || deployState(item).deploying">
                                                <option value="">Select sandbox…</option>
                                                <option v-for="sb in sandboxOptions" :key="sb.value" :value="sb.value">
                                                    {{ sb.label }}
                                                </option>
                                            </select>
                                        </div>
                                        <div class="col-auto" :title="deployBlockedReason(item)">
                                            <button type="button" class="btn btn-primary btn-sm"
                                                :disabled="!deployReady(item)"
                                                @click="createAndDeploy(item)">
                                                <span v-if="deployState(item).creating || deployState(item).deploying"
                                                    class="spinner-border spinner-border-sm me-1" role="status" />
                                                <i v-else class="fa fa-rocket me-1"></i>
                                                <span v-if="deployState(item).creating">Creating…</span>
                                                <span v-else-if="deployState(item).deploying">Deploying…</span>
                                                <span v-else>Create &amp; Deploy</span>
                                            </button>
                                        </div>
                                    </div>
                                    <div v-if="deployBlockedReason(item)" class="small text-muted mt-2">
                                        <i class="fa fa-circle-info me-1" aria-hidden="true"></i>{{ deployBlockedReason(item) }}
                                    </div>
                                    <div v-if="deployState(item).error"
                                        class="alert alert-danger small py-2 px-3 mt-2 mb-0">
                                        {{ deployState(item).error }}
                                    </div>
                                    <div v-if="deployState(item).creating || deployState(item).deploying"
                                        class="deploy-steps mt-3">
                                        <div class="deploy-step"
                                            :class="{ active: deployState(item).creating, done: !deployState(item).creating && deployState(item).deploying }">
                                            <span class="deploy-step__dot"></span>
                                            <span class="deploy-step__label">Create job stack</span>
                                        </div>
                                        <div class="deploy-step" :class="{ active: deployState(item).deploying }">
                                            <span class="deploy-step__dot"></span>
                                            <span class="deploy-step__label">Deploy to sandbox</span>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </template>

                </Tabs>
            </div>

            <!-- Empty state / hero -->
            <div v-else class="dmd-hero">
                <div class="dmd-hero-icon">
                    <i class="fa fa-wand-magic-sparkles" aria-hidden="true"></i>
                </div>
                <template v-if="mode === 'catalog'">
                    <h4 class="dmd-hero-title">Explore your data catalog</h4>
                    <p class="dmd-hero-subtitle">
                        Pick a database above, then scan — or jump straight to what you need.
                    </p>
                    <div class="dmd-hero-suggestions">
                        <button type="button" class="dmd-suggestion" :disabled="!canScanCatalog" @click="runSuggestion('all')">
                            <i class="fa fa-layer-group" aria-hidden="true"></i> Scan the whole database
                        </button>
                        <button type="button" class="dmd-suggestion" :disabled="!canScanCatalog" @click="runSuggestion('lookup')">
                            <i class="fa fa-bolt" aria-hidden="true"></i> Find ZCR lookup–ready tables
                        </button>
                    </div>
                </template>
                <template v-else>
                    <h4 class="dmd-hero-title">Discover the data in your bucket</h4>
                    <p class="dmd-hero-subtitle">
                        Pick a credential and bucket above, optionally narrow by folder, then discover what each
                        dataset is and whether it is ready for ZCR ingestion.
                    </p>
                    <div class="dmd-hero-suggestions">
                        <button type="button" class="dmd-suggestion" :disabled="!filesLocationReady" @click="quickDiscover">
                            <i class="fa fa-magnifying-glass" aria-hidden="true"></i> Discover datasets here
                        </button>
                    </div>
                </template>
            </div>

        </div><!-- end results -->
    </div>
</template>

<script>
/* Discover My Data: runs the agents `discover-my-data` skill and deploys its job stacks through lakeview. */
import axios from "axios";
import { mapActions } from "vuex";
import NavHeader from "../../components/NavHeader.vue";
import Markdown from "../../components/Markdown.vue";
import Tabs from "../../components/Tabs.vue";
import InputField from "../../components/InputField.vue";
import { getAgentStatusIcon, currentStatus } from "../../utils/commonFunction";
import { mergeAgentsQueryIntoUrl } from "../../utils/agentsApi";
import { registerAgentStream, releaseAgentStream } from "../../utils/agentStreamGuard";
import {
    FILE_BROWSER_CRED_SERVICE,
    FILE_BROWSER_CRED_NAMESPACE,
    cloudLabelForCredentialType,
    isBrowsableCredential,
    normalizeBuckets,
    normalizeObjects,
    schemeForCredentialType,
} from "../../utils/fileBrowser";

// Mirrors DMD_MAX_FILE_BYTES in the agents service (the service enforces it).
const MAX_SAMPLE_FILE_BYTES = 5 * 1024 * 1024;
const PLACEHOLDER_RE = /<[A-Z_]+>/g;

const TYPE_LABELS = {
    data_subject_profile: "Profile",
    transaction: "Transaction",
    exposure: "Exposure",
    website_traffic: "Website Traffic",
    customer_interaction: "Customer Interaction",
    unknown: "Unknown",
};

const TYPE_ICONS = {
    data_subject_profile: "fa-id-card",
    transaction: "fa-receipt",
    exposure: "fa-bullhorn",
    website_traffic: "fa-arrow-pointer",
    customer_interaction: "fa-comments",
    unknown: "fa-circle-question",
};

const NO_RECHECK = Object.freeze({
    editing: false, data_type: "", identity_column: "", timestamp_column: "", busy: false, error: "", userSet: false,
});

const SKIP_REASONS = {
    exceeds_max_size: {
        label: "Too large to sample",
        help: "Every file in these datasets is over the size limit. Point the scan at a folder containing a smaller Parquet file.",
    },
    unsupported_format: {
        label: "Not Parquet",
        help: "Discover My Data analyses Parquet files only.",
    },
    dataset_limit_reached: {
        label: "Over the dataset limit",
        help: "At most 10 datasets are analysed per scan. Narrow the folder to include these.",
    },
    size_unknown: { label: "Size unknown", help: "The storage listing returned no size for these files." },
    unreadable: { label: "Could not be read", help: "The sample file could not be previewed." },
};

export default {
    name: "DiscoverMyData",
    components: { NavHeader, Markdown, Tabs, InputField },
    data() {
        return {
            mode: "files",
            // shared: aquacred `filebrowser` credentials (default namespace)
            credentialList: [],
            credentialName: "",
            credentialLoading: false,
            // scan files
            bucketList: [],
            bucket: "",
            bucketsLoading: false,
            prefix: "",
            scanPrompt: "",
            subfolderMenuOpen: false,
            subfolderOptions: [],
            subfoldersLoading: false,
            subfolderError: "",
            subfolderSearch: "",
            // catalog first
            databases: [],
            tables: [],
            database: "",
            table: "",
            databasesLoading: false,
            tablesLoading: false,
            tableStatusFilter: "all",
            tableSearchQuery: "",
            // run
            running: false,
            statusLabel: "",
            // agent steps for the timeline: { label, detail, timestamp }
            statusList: [],
            statusOpen: false,
            errorMessage: "",
            toolbarError: "",
            result: null,
            abortController: null,
            // dataset path / table key -> open detail panel ('columns' | 'preview')
            panels: {},
            // dataset path -> { editing, data_type, identity_column, timestamp_column, busy, error, userSet }
            rechecks: {},
            recheckController: null,
            // stack_name -> { sandboxName, overrides, showPreview, previewTab, creating, deploying, deployed, error }
            deployStates: {},
        };
    },
    computed: {
        credentialOptions() {
            return this.credentialList.map((c) => ({ label: this.credentialLabel(c), value: c.name }));
        },
        bucketOptions() {
            return this.bucketList.map((b) => ({ label: b, value: b }));
        },
        databaseOptions() {
            return this.databases.map((db) => ({ label: db, value: db }));
        },
        tableOptions() {
            return this.tables.map((t) => ({ label: t, value: t }));
        },
        // cleared picker means all tables
        tableModel: {
            get() { return this.table || null; },
            set(v) { this.table = v || ""; },
        },
        credentialPlaceholder() {
            if (this.credentialLoading) return "Loading credentials…";
            return this.credentialOptions.length ? "Select a credential" : "No credentials available";
        },
        bucketPlaceholder() {
            if (this.bucketsLoading) return "Loading buckets…";
            if (!this.credentialName) return "Choose a credential first";
            return this.bucketOptions.length ? "Select a bucket" : "No buckets available";
        },
        filteredSubfolders() {
            const q = this.subfolderSearch.trim().toLowerCase();
            return q ? this.subfolderOptions.filter((o) => o.toLowerCase().includes(q)) : this.subfolderOptions;
        },
        currentStatusText() {
            if (this.running) return this.statusLabel ? `${this.statusLabel}...` : "Loading...";
            return `Done - ${this.statusList.length} steps`;
        },
        statItems() {
            if (this.result?.mode === "catalog") {
                return [
                    { label: "Tables scanned", value: this.catalogTables.length },
                    { label: "Lookup ready", value: this.lookupReadyCount, tone: this.lookupReadyCount ? "ok" : "" },
                    { label: "Iceberg managed", value: this.icebergCount },
                ];
            }
            const items = [{ label: "Datasets", value: this.datasets.length }];
            if (this.result?.scan) items.push({ label: "Objects listed", value: this.result.scan.objects_listed.toLocaleString() });
            items.push({ label: "ZCR ready", value: this.ingestionReadyCount, tone: this.ingestionReadyCount ? "ok" : "" });
            items.push({ label: "Skipped", value: this.skipped.length, tone: this.skipped.length ? "warn" : "" });
            return items;
        },
        runningSteps() {
            return currentStatus(this.statusList);
        },
        cid() {
            return this.$store.state.currentClient?.cid;
        },
        catalogSupported() {
            return String(this.$store.state.selectedCloud || "").toUpperCase() !== "GCP";
        },
        selectedCredential() {
            return this.credentialList.find((c) => c.name === this.credentialName) || null;
        },
        scheme() {
            return schemeForCredentialType(this.selectedCredential?.type);
        },
        storageLabel() {
            if (this.scheme === "gs") return "GCS";
            if (this.scheme === "s3") return "S3";
            return "S3 / GCS";
        },
        maxFileLabel() {
            return this.formatBytes(MAX_SAMPLE_FILE_BYTES);
        },
        normalizedPrefix() {
            const p = this.prefix.replace(/^\/+/, "").replace(/\/+$/, "");
            return p ? `${p}/` : "";
        },
        scanLocationLabel() {
            if (!this.scheme || !this.bucket) return "Choose a credential and bucket above.";
            return `Scanning ${this.scheme}://${this.bucket}/${this.normalizedPrefix}`;
        },
        filesLocationReady() {
            return !this.running && !!this.scheme && !!this.bucket;
        },
        // an empty prompt is blocked by the content safety filter
        canScanFiles() {
            return this.filesLocationReady && !!this.scanPrompt.trim();
        },
        canScanCatalog() {
            return !this.running && this.catalogSupported && !!this.database;
        },
        fileBrowserRoute() {
            const bucket = this.result?.bucket;
            if (!bucket || !this.result?.credential) return null;
            return {
                name: "File Browser List",
                params: { cid: this.cid, bucket },
                query: { namespace: "default", credential: this.result.credential },
            };
        },
        editableTypes() {
            return Object.keys(TYPE_LABELS).filter((t) => t !== "unknown");
        },
        canDeploy() {
            // lakeview restricts job stack create/deploy to aqfer_admin and aqfer_manager
            return this.$store.getters.userRoles.isAccessByRole([1, 1.5]);
        },
        datasets() {
            return this.result?.datasets || [];
        },
        catalogTables() {
            return this.result?.catalog_tables || [];
        },
        skipped() {
            return this.result?.skipped || [];
        },
        ingestionReadyCount() {
            return this.datasets.filter((d) => d.zcr?.ready).length;
        },
        lookupReadyCount() {
            return this.catalogTables.filter((t) => t.lookup?.ready).length;
        },
        icebergCount() {
            return this.catalogTables.filter((t) => t.iceberg_managed).length;
        },
        tableStatusFilters() {
            return [
                { key: "all", label: "All", count: this.catalogTables.length },
                { key: "lookup", label: "Lookup Ready", count: this.lookupReadyCount },
                { key: "not-ready", label: "Not Ready", count: this.catalogTables.length - this.lookupReadyCount },
            ];
        },
        visibleTables() {
            let list = this.catalogTables;
            if (this.tableStatusFilter === "lookup") list = list.filter((t) => t.lookup?.ready);
            else if (this.tableStatusFilter === "not-ready") list = list.filter((t) => !t.lookup?.ready);
            if (this.tableSearchQuery) {
                const q = this.tableSearchQuery.toLowerCase();
                list = list.filter((t) => this.tableKey(t).toLowerCase().includes(q));
            }
            return list;
        },
        skippedGroups() {
            const groups = {};
            for (const s of this.skipped) {
                const meta = SKIP_REASONS[s.reason] || { label: s.reason, help: "" };
                if (!groups[s.reason]) groups[s.reason] = { reason: s.reason, ...meta, items: [] };
                groups[s.reason].items.push(s);
            }
            return Object.values(groups);
        },
        deployableItems() {
            if (this.result?.mode === "catalog") {
                return this.catalogTables
                    .filter((t) => t.job_stack)
                    .map((t) => ({ stack: t.job_stack, label: this.tableKey(t), kind: "Lookup Index" }));
            }
            return this.datasets
                .filter((d) => d.job_stack)
                .map((d) => ({
                    stack: d.job_stack,
                    label: this.datasetName(d.path),
                    kind: `${this.formatDataType(d.data_type)} Ingestion`,
                }));
        },
        tabItems() {
            // structured view first, summary second
            if (this.result?.mode === "catalog") {
                return [
                    { name: "Tables", id: "dmd-tables", slot: "tables" },
                    { name: "Summary", id: "dmd-summary", slot: "summary" },
                    { name: "Deploy Pipeline", id: "dmd-deploy", slot: "deploy" },
                ];
            }
            return [
                { name: "Datasets", id: "dmd-datasets", slot: "datasets" },
                { name: "Summary", id: "dmd-summary", slot: "summary" },
                { name: `Skipped (${this.skipped.length})`, id: "dmd-skipped", slot: "skipped" },
                { name: "Deploy Pipeline", id: "dmd-deploy", slot: "deploy" },
            ];
        },
        sandboxOptions() {
            return (this.$store.state.sandbox?.sandboxes || []).map((sb) => ({
                label: sb.env ? `${sb.name} (${sb.env})` : sb.name,
                value: sb.name,
            }));
        },
    },
    watch: {
        cid(to, from) {
            if (to && from && to !== from) {
                this.cancel();
                this.resetResult();
                this.databases = [];
                this.database = "";
                this.loadCredentials();
                if (this.mode === "catalog") this.loadDatabases();
            }
        },
        credentialName() {
            this.bucket = "";
            this.bucketList = [];
            if (this.credentialName && this.mode === "files") this.loadBuckets();
        },
        bucket() {
            this.prefix = "";
            this.subfolderMenuOpen = false;
        },
        async database(db) {
            this.table = "";
            this.tables = [];
            if (!db) return;
            this.tablesLoading = true;
            try {
                const resp = await axios.get(`${this.pailBase()}/dbs/${encodeURIComponent(db)}/tables`, this.authHeaders());
                this.tables = Array.isArray(resp.data?.tables) ? resp.data.tables : [];
            } catch (e) {
                this.toolbarError = this.errorText(e, "Failed to load tables.");
            } finally {
                this.tablesLoading = false;
            }
        },
        catalogSupported(supported) {
            if (!supported && this.mode === "catalog") this.setMode("files");
        },
    },
    methods: {
        ...mapActions({
            fetchAquacredCredentials: "amdp/fetchAquacredCredentials",
            getBucketList: "amdp/getBucketList",
            getFileBrowserList: "amdp/getFileBrowserList",
            upsertJobStack: "amdp/upsertJobStack",
            deployJobStack: "amdp/deployJobStack",
        }),
        formatDataType(type) {
            return TYPE_LABELS[type] || type;
        },
        typeHintDiffers(ds) {
            const hint = ds.details?.heuristic_data_type;
            if (this.rechecks[ds.path]?.userSet) return false;
            return !!hint && hint !== "unknown" && hint !== ds.data_type;
        },
        togglePanel(key, panel) {
            this.panels = { ...this.panels, [key]: this.panels[key] === panel ? "" : panel };
        },
        emptyPercent(column, sampled) {
            if (!sampled) return "—";
            return `${Math.round((100 * (column.nulls || 0)) / sampled)}%`;
        },
        formatCell(value) {
            if (value == null) return "∅";
            return typeof value === "object" ? JSON.stringify(value) : String(value);
        },
        formatTableType(type) {
            return (type || "").toLowerCase().replace(/_/g, " ");
        },
        tableKey(t) {
            return `${t.database}.${t.table}`;
        },
        datasetName(path) {
            // "" is a table whose partitions start at the bucket root
            return (path || "").replace(/\/+$/, "").split("/").pop() || this.result?.bucket || path;
        },
        storageUri(path) {
            return `${this.result?.storage_type || this.scheme}://${this.result?.bucket || this.bucket}/${path}`;
        },
        sampleFileName(ds) {
            if (!ds.sample_key) return "—";
            const base = `${ds.path.replace(/\/+$/, "")}/`;
            return ds.sample_key.startsWith(base) ? ds.sample_key.slice(base.length) : ds.sample_key;
        },
        getAgentStatusIcon,
        typeIcon(type) {
            return TYPE_ICONS[type] || TYPE_ICONS.unknown;
        },
        credentialLabel(cred) {
            const cloud = cloudLabelForCredentialType(cred.type);
            return cloud ? `${cred.name} · ${cloud}` : cred.name;
        },
        formatBytes(bytes) {
            if (bytes == null) return "";
            if (bytes < 1024) return `${bytes} B`;
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        },
        errorText(e, fallback) {
            return e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback;
        },
        authHeaders() {
            return { headers: { Authorization: `Bearer ${this.$store.state.accessToken}` } };
        },
        pailBase() {
            return `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.cid}`;
        },
        setMode(mode) {
            if (this.mode === mode) return;
            // switching modes invalidates the other mode's results
            this.mode = mode;
            this.resetResult();
            this.toolbarError = "";
            if (mode === "catalog" && !this.databases.length) this.loadDatabases();
            if (mode === "files" && this.credentialName && !this.bucketList.length) this.loadBuckets();
        },
        resetResult() {
            // a recheck still running belongs to the result being replaced
            this.recheckController?.abort();
            this.result = null;
            this.errorMessage = "";
            // the steps belong to the result being cleared
            this.statusList = [];
            this.statusOpen = false;
            this.deployStates = {};
            this.panels = {};
            this.rechecks = {};
            this.tableStatusFilter = "all";
            this.tableSearchQuery = "";
        },

        // ── credentials / buckets / folders (pail v2 file browser) ──────────
        async loadCredentials() {
            this.credentialLoading = true;
            this.credentialList = [];
            this.credentialName = "";
            try {
                const response = await this.fetchAquacredCredentials({
                    service: FILE_BROWSER_CRED_SERVICE,
                    namespace: FILE_BROWSER_CRED_NAMESPACE,
                    mutation: "setAquacredCredentials",
                });
                // cache hit returns the list, a fresh read the response
                const credentials = Array.isArray(response)
                    ? response
                    : this.$store.state.amdp.aquacredCredentials?.[FILE_BROWSER_CRED_SERVICE]?.[FILE_BROWSER_CRED_NAMESPACE]?.credentials || [];
                // only object-store credentials can browse buckets
                this.credentialList = credentials.filter(isBrowsableCredential);
                if (this.credentialList.length === 1) this.credentialName = this.credentialList[0].name;
            } catch (e) {
                this.toolbarError = this.errorText(e, "Failed to load credentials.");
            } finally {
                this.credentialLoading = false;
            }
        },
        async loadBuckets() {
            this.bucketsLoading = true;
            this.toolbarError = "";
            try {
                const response = await this.getBucketList({ credential: this.credentialName });
                this.bucketList = normalizeBuckets(response.data).map((b) => b.name);
            } catch (e) {
                this.toolbarError = this.errorText(e, "Failed to load buckets.");
            } finally {
                this.bucketsLoading = false;
            }
        },
        async toggleSubfolderMenu() {
            this.subfolderMenuOpen = !this.subfolderMenuOpen;
            if (this.subfolderMenuOpen) await this.loadSubfolders();
        },
        async loadSubfolders() {
            this.subfoldersLoading = true;
            this.subfolderError = "";
            this.subfolderOptions = [];
            this.subfolderSearch = "";
            try {
                const resp = await this.getFileBrowserList({
                    credential: this.credentialName,
                    bucket: this.bucket,
                    prefix: this.normalizedPrefix,
                });
                this.subfolderOptions = normalizeObjects(resp.data?.items, this.normalizedPrefix)
                    .filter((o) => o.is_dir && o.folderName)
                    .map((o) => o.folderName);
            } catch (e) {
                this.subfolderError = this.errorText(e, "Failed to list folders.");
            } finally {
                this.subfoldersLoading = false;
            }
        },
        async selectSubfolder(name) {
            this.prefix = `${this.normalizedPrefix}${name}/`;
            await this.loadSubfolders();
        },
        async goUpSubfolder() {
            const parts = this.normalizedPrefix.split("/").filter(Boolean);
            parts.pop();
            this.prefix = parts.length ? `${parts.join("/")}/` : "";
            await this.loadSubfolders();
        },

        // ── catalog ────────────────────────────────────────────────────────
        async loadDatabases() {
            if (!this.catalogSupported) return;
            this.databasesLoading = true;
            this.toolbarError = "";
            try {
                const resp = await axios.get(`${this.pailBase()}/dbs`, this.authHeaders());
                this.databases = Array.isArray(resp.data) ? resp.data : [];
            } catch (e) {
                this.toolbarError = this.errorText(e, "Failed to load databases.");
            } finally {
                this.databasesLoading = false;
            }
        },
        async runSuggestion(statusFilter) {
            this.table = "";
            await this.run();
            if (this.result) this.tableStatusFilter = statusFilter;
        },

        // ── run ────────────────────────────────────────────────────────────
        skillInputs() {
            if (this.mode === "catalog") {
                return {
                    mode: "catalog",
                    database: this.database,
                    table: this.table,
                    credential: this.credentialName || null,
                };
            }
            return {
                mode: "files",
                credential: this.credentialName,
                storage_type: this.scheme,
                bucket: this.bucket,
                prefix: this.normalizedPrefix,
                prompt: this.scanPrompt.trim(),
            };
        },
        // Runs the skill and returns its final output; status events go to onStatus
        async streamSkill(skillInputs, signal, onStatus) {
            const url = mergeAgentsQueryIntoUrl(
                `${this.$store.state.agentsUrl}/v1/cids/${this.cid}/skills/discover-my-data/execute`,
                this.$store.state,
            );
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "text/event-stream",
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                },
                body: JSON.stringify({ skill_inputs: skillInputs }),
                signal,
            });
            if (!response.ok) {
                let detail = "";
                try {
                    detail = (await response.json())?.message || "";
                } catch (_) { /* not JSON */ }
                throw new Error(detail || `Server returned ${response.status} ${response.statusText}`);
            }

            let finalOutput = null;
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            for (;;) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const events = buffer.split("\n\n");
                buffer = events.pop() || "";
                for (const raw of events) {
                    const eventType = raw.match(/^event: (.+)$/m)?.[1];
                    const data = raw.match(/^data: (.+)$/m)?.[1];
                    if (!eventType || data == null) continue;
                    if (eventType === "status") {
                        onStatus?.(JSON.parse(data));
                    } else if (eventType === "message") {
                        // growing JSON prefixes; only the complete one parses
                        try {
                            finalOutput = JSON.parse(data);
                        } catch (_) { /* partial chunk */ }
                    } else if (eventType === "blocked") {
                        throw new Error("Your request was blocked by the content safety filter.");
                    } else if (eventType === "error") {
                        throw new Error(JSON.parse(data)?.message || "The agent reported an error.");
                    }
                }
            }
            if (!finalOutput) throw new Error("Discovery finished without a result. Please try again.");
            return finalOutput;
        },
        // quick start: fills a default prompt when the user gave none
        quickDiscover() {
            if (!this.scanPrompt.trim()) {
                this.scanPrompt = "Find the datasets in this folder and check which are ready for ZCR ingestion.";
            }
            this.run();
        },
        async run() {
            this.resetResult();
            this.subfolderMenuOpen = false;
            this.running = true;
            this.statusLabel = "";
            this.statusList = [];
            this.statusOpen = false;
            this.abortController = new AbortController();
            registerAgentStream(this, "Discover My Data", () => this.cancel());
            try {
                this.result = await this.streamSkill(this.skillInputs(), this.abortController.signal, (status) => {
                    this.statusLabel = status.label;
                    this.statusList.push({
                        label: status.label,
                        detail: status.detail || "",
                        timestamp: status.timestamp || new Date().toISOString().replace(/T/, " ").replace(/Z/, ""),
                    });
                });
                this.syncDeployStates();
            } catch (e) {
                if (e.name !== "AbortError") {
                    this.errorMessage = e.message || String(e);
                    this.$store.dispatch("addToast", { type: "error", message: this.errorMessage });
                }
            } finally {
                this.running = false;
                this.abortController = null;
                releaseAgentStream(this);
            }
        },

        // ── recheck: the user's own data type / columns ────────────────────
        recheckState(ds) {
            // read-only default until editing starts (not created during render)
            return this.rechecks[ds.path] || NO_RECHECK;
        },
        startRecheck(ds) {
            this.rechecks[ds.path] = {
                ...(this.rechecks[ds.path] || NO_RECHECK),
                editing: true,
                error: "",
                data_type: ds.data_type === "unknown" ? "" : ds.data_type,
                identity_column: ds.identity_column || "",
                timestamp_column: ds.timestamp_column || "",
            };
        },
        async recheck(ds) {
            const state = this.recheckState(ds);
            if (!state.data_type) {
                state.error = "Choose a data type.";
                return;
            }
            state.busy = true;
            state.error = "";
            this.recheckController = new AbortController();
            try {
                const output = await this.streamSkill({
                    mode: "recheck",
                    credential: this.result.credential,
                    storage_type: this.result.storage_type,
                    bucket: this.result.bucket,
                    dataset: {
                        path: ds.path,
                        sample_key: ds.sample_key,
                        columns: ds.columns,
                        data_type: state.data_type,
                        identity_column: state.identity_column || null,
                        timestamp_column: state.timestamp_column || null,
                    },
                }, this.recheckController.signal);
                const updated = (output.datasets || []).find((d) => d.path === ds.path);
                if (!updated) throw new Error("The recheck returned no result for this dataset.");
                // a new scan may have replaced the result
                const idx = this.result?.datasets?.findIndex((d) => d.path === ds.path) ?? -1;
                if (idx < 0) return;
                // rebuild this stack's deploy form; its vars have the old columns
                if (ds.job_stack) delete this.deployStates[ds.job_stack.stack_name];
                // recheck doesn't re-read the bucket, so keep the scan's details
                this.result.datasets.splice(idx, 1, { ...ds, ...updated, details: ds.details });
                this.syncDeployStates();
                state.userSet = true;
                state.editing = false;
            } catch (e) {
                if (e.name !== "AbortError") state.error = e.message || String(e);
            } finally {
                state.busy = false;
                this.recheckController = null;
            }
        },
        cancel() {
            this.abortController?.abort();
            this.recheckController?.abort();
        },

        // ── deploy ─────────────────────────────────────────────────────────
        syncDeployStates() {
            // keeps what the user typed for stacks still offered
            const states = {};
            for (const item of this.deployableItems) {
                states[item.stack.stack_name] = this.deployStates[item.stack.stack_name] || {
                    sandboxName: "",
                    overrides: item.stack.deploy_request.overrides.map((o) => ({ ...o })),
                    showPreview: false,
                    previewTab: "Create Request",
                    creating: false,
                    deploying: false,
                    deployed: false,
                    error: "",
                };
            }
            this.deployStates = states;
        },
        deployState(item) {
            return this.deployStates[item.stack.stack_name];
        },
        deployBody(item) {
            const state = this.deployState(item);
            return {
                ...item.stack.deploy_request,
                cid: this.cid,
                sandbox: state.sandboxName || item.stack.deploy_request.sandbox,
                overrides: state.overrides,
            };
        },
        stackJson(item) {
            return JSON.stringify({
                create_request: item.stack.create_request,
                deploy_request: this.deployBody(item),
            }, null, 2);
        },
        async copyStack(item) {
            try {
                await navigator.clipboard.writeText(this.stackJson(item));
                this.$store.dispatch("addToast", { type: "success", message: "Job stack copied to the clipboard." });
            } catch (e) {
                this.$store.dispatch("addToast", { type: "error", message: "Could not copy to the clipboard." });
            }
        },
        downloadStack(item) {
            const blob = new Blob([this.stackJson(item)], { type: "application/json" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${item.stack.stack_name}.json`;
            link.click();
            URL.revokeObjectURL(link.href);
        },
        remainingPlaceholders(item) {
            const found = new Set();
            for (const o of this.deployState(item).overrides) {
                for (const m of o.vars.match(PLACEHOLDER_RE) || []) found.add(m);
            }
            return [...found];
        },
        deployBlockedReason(item) {
            const state = this.deployState(item);
            if (state.creating || state.deploying || state.deployed) return "";
            const left = this.remainingPlaceholders(item);
            if (left.length) return `Replace ${left.join(", ")} in the vars above to enable Create & Deploy.`;
            if (!state.sandboxName) return "Select a target sandbox to enable Create & Deploy.";
            return "";
        },
        deployReady(item) {
            const state = this.deployState(item);
            return this.canDeploy && !state.creating && !state.deploying && !!state.sandboxName
                && !this.remainingPlaceholders(item).length;
        },
        async createAndDeploy(item) {
            const state = this.deployState(item);
            if (!this.deployReady(item)) return;
            state.error = "";
            state.creating = true;
            try {
                await this.upsertJobStack(item.stack.create_request);
                state.creating = false;
                state.deploying = true;
                await this.deployJobStack({ name: item.stack.stack_name, data: this.deployBody(item) });
                state.deployed = true;
                this.$store.dispatch("addToast", {
                    type: "success",
                    message: `Pipeline "${item.stack.stack_name}" deployed to ${state.sandboxName}.`,
                });
            } catch (e) {
                state.error = this.errorText(e, "Failed to create or deploy the job stack.");
            } finally {
                state.creating = false;
                state.deploying = false;
            }
        },
    },
    mounted() {
        this.loadCredentials();
        if (!this.$store.state.sandbox?.sandboxes?.length) this.$store.dispatch("sandbox/fetchSandboxes");
    },
    beforeUnmount() {
        this.cancel();
        releaseAgentStream(this);
    },
};
</script>

<style lang="scss" scoped>
@use '../../theme/scss/_user-variables.scss' as *;

/* ── Mode switch: catalog-first vs. file-browser-first ────────────────── */
.dmd-mode-switch {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}
.dmd-mode-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.82rem;
    font-weight: 600;
    padding: 0.4rem 0.9rem;
    border-radius: 0.5rem;
    border: 1px solid rgba(108, 117, 125, 0.25);
    background: var(--bs-body-bg, #fff);
    color: var(--bs-secondary, #6c757d);
    cursor: pointer;
    transition: all 0.15s;

    &:hover:not(:disabled) { border-color: var(--bs-primary, #2c7be5); }
    &.active {
        background: var(--bs-primary, #2c7be5);
        border-color: var(--bs-primary, #2c7be5);
        color: #fff;
    }
    &:disabled { opacity: 0.55; cursor: not-allowed; }
}
.dmd-mode-hint {
    font-size: 0.76rem;
    color: var(--bs-secondary, #6c757d);
    opacity: 0.85;
}

/* ── Toolbar: labelled pickers, as in the file browser ──────────────── */
.dmd-toolbar-card {
    border-radius: 0.625rem;
}
.dmd-controls-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
    gap: 1rem;
    align-items: end;
}
/* scan files has no button in this row */
.dmd-controls-grid--files {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}
@media (max-width: 991.98px) {
    .dmd-controls-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 575.98px) {
    .dmd-controls-grid { grid-template-columns: minmax(0, 1fr); }
}
.dmd-field {
    min-width: 0;
    :deep(.form-group) { margin-bottom: 0; }
}
/* inputs match the searchable pickers' height */
.dmd-folder .form-control,
.dmd-folder .btn {
    height: 2.5rem;
}
.dmd-ai-note {
    font-size: 0.72rem;
    opacity: 0.8;
}
.dmd-toolbar-body {
    padding: 1rem 1.25rem;
}
.dmd-prompt-row {
    display: flex;
    gap: 0.75rem;
    .form-control { height: 2.5rem; }
}
.dmd-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 0.35rem;
}

/* ── Folder picker (browse button + searchable subfolder menu) ──────── */
.dmd-folder {
    position: relative;
}
.dmd-subfolder-menu {
    position: absolute;
    top: calc(100% + 0.3rem);
    left: 0;
    z-index: 20;
    min-width: 220px;
    max-width: 320px;
    max-height: 260px;
    overflow-y: auto;
    background: var(--bs-body-bg, #fff);
    border: 1px solid rgba(108, 117, 125, 0.25);
    border-radius: 0.5rem;
    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.12);
    padding: 0.3rem;
}
.dmd-subfolder-item {
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    font-size: 0.82rem;
    padding: 0.35rem 0.5rem;
    border: none;
    background: transparent;
    border-radius: 0.35rem;
    color: var(--bs-body-color);
    cursor: pointer;

    &:hover { background: rgba(44, 123, 229, 0.08); }
    .fa { opacity: 0.6; flex-shrink: 0; }
}
.dmd-subfolder-item--up {
    color: var(--bs-secondary, #6c757d);
    border-bottom: 1px solid rgba(108, 117, 125, 0.14);
    border-radius: 0;
    margin-bottom: 0.2rem;
    padding-bottom: 0.45rem;
}
.dmd-subfolder-msg {
    font-size: 0.8rem;
    padding: 0.4rem 0.5rem;
}
.dmd-scan-btn {
    white-space: nowrap;
    padding: 0.4rem 1.2rem;
    height: 2.5rem;
}

/* ── Hero empty state with quick-start suggestions ───────────────────── */
.dmd-hero {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 4rem 1.5rem 3.5rem;
}
.dmd-hero-icon {
    width: 3.75rem;
    height: 3.75rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(44, 123, 229, 0.12);
    color: #2c7be5;
    font-size: 1.5rem;
    margin-bottom: 1.25rem;
}
.dmd-hero-title {
    font-weight: 700;
    margin-bottom: 0.4rem;
}
.dmd-hero-subtitle {
    color: var(--bs-secondary, #6c757d);
    max-width: 32rem;
    margin-bottom: 1.75rem;
}
.dmd-hero-suggestions {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
    justify-content: center;
}
.dmd-suggestion {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.84rem;
    font-weight: 500;
    padding: 0.5rem 1.1rem;
    border-radius: 999px;
    border: 1px solid rgba(44, 123, 229, 0.25);
    background: rgba(44, 123, 229, 0.06);
    color: var(--bs-primary, #2c7be5);
    cursor: pointer;
    transition: all 0.15s;

    &:hover:not(:disabled) {
        background: rgba(44, 123, 229, 0.14);
        border-color: rgba(44, 123, 229, 0.4);
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    .fa { font-size: 0.78rem; opacity: 0.8; }
}

/* ── Scan totals: one slim bar ─────────────────────────────────────── */
.dmd-statbar {
    display: flex;
    flex-wrap: wrap;
    background: var(--bs-body-bg, #fff);
    border: 1px solid rgba(108, 117, 125, 0.14);
    border-radius: 0.5rem;
}
.dmd-stat {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    padding: 0.6rem 1.25rem;
    & + & { border-left: 1px solid rgba(108, 117, 125, 0.14); }
}
.dmd-stat-value {
    font-size: 1.15rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--bs-body-color);
}
.dmd-stat-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--bs-secondary, #6c757d);
}
.dmd-stat--ok .dmd-stat-value { color: #009f60; }
.dmd-stat--warn .dmd-stat-value { color: #b7791f; }

/* ── Tables tab: filter chips + search ───────────────────────────────── */
.dmd-table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
}
.dmd-filter-chips {
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
}
.dmd-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.76rem;
    font-weight: 500;
    padding: 0.3rem 0.7rem;
    border-radius: 999px;
    border: 1px solid rgba(108, 117, 125, 0.25);
    background: var(--bs-body-bg, #fff);
    color: var(--bs-body-color);
    cursor: pointer;
    transition: all 0.15s;

    &:hover { border-color: var(--bs-primary, #2c7be5); }
    &.active {
        background: var(--bs-primary, #2c7be5);
        border-color: var(--bs-primary, #2c7be5);
        color: #fff;
        .dmd-chip-count { background: rgba(255, 255, 255, 0.25); color: #fff; }
    }
}
.dmd-chip-count {
    background: rgba(108, 117, 125, 0.14);
    color: var(--bs-secondary, #6c757d);
    border-radius: 999px;
    padding: 0.05rem 0.4rem;
    font-size: 0.68rem;
    font-variant-numeric: tabular-nums;
}
.dmd-table-search {
    position: relative;
    min-width: 220px;

    .fa-magnifying-glass {
        position: absolute;
        left: 0.65rem;
        top: 50%;
        transform: translateY(-50%);
        font-size: 0.75rem;
        color: var(--bs-secondary, #6c757d);
        opacity: 0.6;
        pointer-events: none;
    }
    input { padding-left: 1.8rem; }
}
.dmd-table-card {
    transition: box-shadow 0.15s;
}

/* ── Results area ────────────────────────────────────────────────────── */
.discover-card {
    border-radius: 0.5rem;
}

/* agent step timeline, same look as Map My Data */
.dmd-agent-status {
    display: flex;
    align-items: center;
    gap: 8px;
}
.status-text {
    font-size: 13px;
}
.dmd-status-list {
    max-height: 300px;
    overflow-y: auto;
    .border-bottom { padding-top: 0.375rem; padding-bottom: 0.375rem; }
    .border-bottom:last-child { border-bottom: 0 !important; }
}

/* ── Summary markdown ────────────────────────────────────────────────── */
.discover-markdown {
    :deep(h2) { border-bottom: 1px solid #dee2e6; padding-bottom: 0.35rem; margin-bottom: 0.75rem; }
    :deep(h3) { padding: 0.4rem 0.75rem; background: #edf2f9; border-radius: 4px; margin-bottom: 0.5rem; }
    :deep(table td), :deep(table th) { padding: 0.5em; }
    :deep(pre) { background: #f8f9fa; border-radius: 6px; padding: 1rem; font-size: 0.8125rem; overflow-x: auto; }
    :deep(code):not(pre code) { background: rgba(44, 123, 229, 0.08); color: #2c7be5; border-radius: 3px; padding: 0.1em 0.3em; }
    /* auto-linked s3:// / gs:// paths aren't navigable, so style them as code */
    :deep(a) {
        color: var(--bs-body-color);
        background: rgba(108, 117, 125, 0.08);
        text-decoration: none;
        font-family: var(--bs-font-monospace, monospace);
        font-size: 0.85em;
        padding: 0.05em 0.35em;
        border-radius: 3px;
        word-break: break-all;
    }
    :deep(a:hover) { text-decoration: underline; }
}

/* ── Card accent: a left stripe, green when ZCR ready ─────────────────── */
.dmd-card-accent {
    border-left: 4px solid rgba(108, 117, 125, 0.25);
}
.dmd-card-accent--ready {
    border-left-color: #00bf73;
}

/* ── Dataset card ───────────────────────────────────────────────────── */
.dmd-ds-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding-top: 0.75rem;
}
.dmd-ds-card {
    margin-bottom: 0;
    border: 1px solid rgba(108, 117, 125, 0.14);
    border-left: 3px solid rgba(108, 117, 125, 0.3);
    border-radius: 0.5rem;
    .card-body { padding: 1rem 1.25rem; }
}
.dmd-ds-card--ready {
    border-left-color: #00bf73;
}
.dmd-ds-head {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    flex-wrap: wrap;
}
.dmd-ds-icon {
    flex-shrink: 0;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
    color: #2c7be5;
    background: rgba(44, 123, 229, 0.1);
}
.dmd-ds-icon--transaction { color: #009f60; background: rgba(0, 191, 115, 0.1); }
.dmd-ds-icon--exposure { color: #b7791f; background: rgba(246, 195, 67, 0.15); }
.dmd-ds-icon--website_traffic { color: #6f42c1; background: rgba(111, 66, 193, 0.1); }
.dmd-ds-icon--customer_interaction { color: #d6336c; background: rgba(214, 51, 108, 0.1); }
.dmd-ds-icon--unknown { color: #6c757d; background: rgba(108, 117, 125, 0.12); }
.dmd-ds-title {
    flex: 1;
    min-width: 14rem;
    h5 { font-size: 1rem; font-weight: 600; }
}
.dmd-ds-path {
    font-size: 0.78rem;
    margin-top: 0.15rem;
    code { color: var(--bs-secondary, #6c757d); background: transparent; padding: 0; word-break: break-all; }
    a { font-size: 0.72rem; }
}
.dmd-ds-badges {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
}
.dmd-ds-action {
    font-size: 0.75rem;
    padding: 0.2rem 0.6rem;
}
.dmd-ds-reason {
    font-size: 0.82rem;
    color: var(--bs-secondary, #6c757d);
    margin: 0.75rem 0 0;
}
.dmd-ds-note {
    font-size: 0.78rem;
    font-style: italic;
    color: var(--bs-secondary, #6c757d);
    margin: 0.35rem 0 0;
}
.dmd-facts {
    display: flex;
    flex-wrap: wrap;
    margin-top: 0.85rem;
    border: 1px solid rgba(108, 117, 125, 0.14);
    border-radius: 0.375rem;
    background: rgba(108, 117, 125, 0.03);
}
.dmd-fact {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    padding: 0.5rem 1rem;
    font-size: 0.82rem;
    & + & { border-left: 1px solid rgba(108, 117, 125, 0.14); }
    .ds-label { margin-bottom: 0; }
    code { font-size: 0.8rem; }
}
.dmd-issues {
    margin: 0.5rem 0 0;
    padding-left: 1.1rem;
    font-size: 0.8rem;
    color: #856404;
}
.dmd-sample-file {
    align-self: center;
    font-size: 0.75rem;
    color: var(--bs-secondary, #6c757d);
    max-width: 20rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ── Recheck editor ──────────────────────────────────────────────────── */
.dmd-recheck {
    padding: 0.75rem;
    background: rgba(44, 123, 229, 0.04);
    border: 1px solid rgba(44, 123, 229, 0.18);
    border-radius: 0.375rem;
}

/* ── ZCR check cards ─────────────────────────────────────────────────── */
.zcr-check-card {
    height: 100%;
    padding: 0.6rem 0.75rem;
    background: rgba(108, 117, 125, 0.04);
    border: 1px solid rgba(108, 117, 125, 0.14);
    border-radius: 0.375rem;
}

/* ── Skipped file list ───────────────────────────────────────────────── */
.dmd-file-list {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
}
.dmd-file-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.3rem 0.5rem;
    background: var(--bs-body-bg, #fff);
    border: 1px solid rgba(108, 117, 125, 0.12);
    border-radius: 4px;
    code { word-break: break-all; }
}

/* ── Dataset / table badges ──────────────────────────────────────────── */
.ds-badge {
    display: inline-flex;
    align-items: center;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    white-space: nowrap;
}
.ds-badge--format { background: rgba(108, 117, 125, 0.12); color: #6c757d; }
.ds-badge--zcr-ok {
    background: rgba(0, 191, 115, 0.12);
    color: #009f60;
    border: 1px solid rgba(0, 159, 96, 0.3);
}
.ds-badge--zcr-warn {
    background: rgba(255, 193, 7, 0.12);
    color: #856404;
    border: 1px solid rgba(255, 193, 7, 0.35);
}
.ds-badge--type {
    background: rgba(44, 123, 229, 0.1);
    color: #2c7be5;
    border: 1px solid rgba(44, 123, 229, 0.2);
}
.ds-label {
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--bs-secondary, #6c757d);
    margin-bottom: 0.2rem;
}
.ds-location {
    font-size: 0.8rem;
    word-break: break-all;
    color: var(--bs-body-color);
    background: transparent;
    padding: 0;
}
.ds-col-badge {
    display: inline-block;
    font-size: 0.72rem;
    padding: 0.15rem 0.5rem;
    background: rgba(44, 123, 229, 0.06);
    border: 1px solid rgba(44, 123, 229, 0.18);
    border-radius: 4px;
    color: #2c5fa8;
    font-family: var(--bs-font-monospace, monospace);
}
.ds-col-badge--key {
    background: rgba(0, 191, 115, 0.1);
    border-color: rgba(0, 159, 96, 0.35);
    color: #009f60;
    font-weight: 600;
}

/* ── Dataset / table detail: stats line, column profile, sample rows ─── */
.dmd-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem 1.1rem;
    font-size: 0.8rem;
    color: var(--bs-secondary, #6c757d);
    .fa { opacity: 0.7; }
}
.dmd-detail-table {
    max-height: 320px;
    overflow: auto;
    border: 1px solid rgba(108, 117, 125, 0.14);
    border-radius: 0.375rem;
    th { position: sticky; top: 0; background: var(--bs-body-bg, #fff); font-size: 0.72rem; white-space: nowrap; }
    td { font-size: 0.8rem; vertical-align: top; }
}
.dmd-examples { max-width: 28rem; word-break: break-all; }

/* ── Deploy section ──────────────────────────────────────────────────── */
.deploy-pipeline-name { font-size: 0.875rem; color: var(--bs-body-color); }
.discover-vars { font-family: var(--bs-font-monospace, monospace); font-size: 0.78rem; }
.deploy-config-tabs { display: flex; gap: 0.25rem; }
.deploy-config-tab {
    padding: 0.25rem 0.75rem;
    font-size: 0.78rem;
    font-weight: 500;
    border: 1px solid #d0d7e0;
    border-radius: 4px;
    background: #f8f9fa;
    color: #6c757d;
    cursor: pointer;
    transition: all 0.15s;
    &.active {
        background: var(--bs-primary, #2c7be5);
        border-color: var(--bs-primary, #2c7be5);
        color: #fff;
    }
}
.deploy-steps { display: flex; flex-direction: column; gap: 0.5rem; padding-left: 0.25rem; }
.deploy-step {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 0.8125rem;
    color: #adb5bd;
    &.active { color: var(--bs-primary, #2c7be5); }
    &.done   { color: #00bf73; }
}
.deploy-step__dot {
    width: 0.55rem;
    height: 0.55rem;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
}
.deploy-step.active .deploy-step__dot {
    animation: pulse-dot 1s ease-in-out infinite;
}
@keyframes pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.4; transform: scale(0.7); }
}

/* ── Code block ──────────────────────────────────────────────────────── */
.discover-code-block {
    background: rgba(108, 117, 125, 0.06);
    border: 1px solid rgba(108, 117, 125, 0.18);
    border-radius: 0.375rem;
    padding: 0.875rem 1rem;
    font-size: 0.78rem;
    line-height: 1.6;
    overflow-x: auto;
    white-space: pre;
    max-height: 380px;
    overflow-y: auto;
}

/* ── Loading skeleton ────────────────────────────────────────────────── */
@keyframes shine {
    to { background-position-x: -200%; }
}
.loading-content-card {
    background: #fff;
    border-radius: 5px;
    .content { padding: 15px; }
    h2, p {
        background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
        border-radius: 5px;
        background-size: 200% 100%;
        animation: 1.5s shine linear infinite;
    }
    h2 { height: 30px; margin-bottom: 12px; }
    p  { height: 70px; }
}
.loading-content-card-dark {
    background: $dark-card-bg;
    border-radius: 5px;
    .content { padding: 15px; }
    h2, p {
        background: linear-gradient(110deg, $dark-elevated-bg 8%, $dark-border-color 18%, $dark-elevated-bg 33%);
        border-radius: 5px;
        background-size: 200% 100%;
        animation: 1.5s shine linear infinite;
    }
    h2 { height: 30px; margin-bottom: 12px; }
    p  { height: 70px; }
}
</style>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .discover-markdown {
        h3 { background: $dark-elevated-bg; }
        pre { background: $dark-elevated-bg; }
        a { background: rgba(255,255,255,0.06); }
    }
    .zcr-check-card { background: rgba(255,255,255,0.03); border-color: $dark-border-color; }
    .dmd-file-row { background: $dark-elevated-bg; border-color: $dark-border-color; }
    .discover-code-block { background: $dark-elevated-bg; border-color: $dark-border-color; }
    .deploy-config-tab { background: $dark-elevated-bg; border-color: $dark-border-color; color: #adb5bd; }
    .deploy-config-tab.active { background: var(--bs-primary, #2c7be5); border-color: var(--bs-primary, #2c7be5); color: #fff; }
    .dmd-statbar, .dmd-ds-card { background: $dark-elevated-bg; border-color: $dark-border-color; }
    .dmd-ds-card--ready { border-left-color: #00bf73; }
    .dmd-stat + .dmd-stat, .dmd-fact + .dmd-fact { border-left-color: $dark-border-color; }
    .dmd-facts { background: rgba(255,255,255,0.03); border-color: $dark-border-color; }
    .dmd-chip { background: $dark-elevated-bg; border-color: $dark-border-color; color: #adb5bd; }
    .dmd-chip-count { background: rgba(255,255,255,0.08); }
    .dmd-mode-btn { background: $dark-elevated-bg; border-color: $dark-border-color; color: #adb5bd; }
    .dmd-mode-btn.active { background: var(--bs-primary, #2c7be5); border-color: var(--bs-primary, #2c7be5); color: #fff; }
    .dmd-subfolder-menu { background: $dark-elevated-bg; border-color: $dark-border-color; }
    .dmd-recheck { background: rgba(255,255,255,0.03); border-color: $dark-border-color; }
    .dmd-detail-table { border-color: $dark-border-color; th { background: $dark-card-bg; } }
    .ds-col-badge { background: rgba(255,255,255,0.04); border-color: $dark-border-color; color: #8eb4f8; }
    .dmd-subfolder-menu { background: $dark-elevated-bg; border-color: $dark-border-color; }
    .dmd-subfolder-item:hover { background: rgba(255,255,255,0.06); }
    .dmd-subfolder-item--up { border-color: $dark-border-color; }
}
</style>
